import { clockKey, createClockKey, mergeFields, newId, type Mutation } from '@tahi/core';
import { history, outbox } from '@tahi/db';
import { eq, getTableColumns, getTableName, inArray } from 'drizzle-orm';
import type { BatchItem } from 'drizzle-orm/batch';
import type { SQLiteTable } from 'drizzle-orm/sqlite-core';
import { encodeRow, pickColumns, readClock, type Row } from './columns';
import type { LocalDb } from './localDb';
import { withWriteLock } from './lock';
import { setStateQuery } from './syncState';
import { emitTables } from './tableBus';
import { tableFor } from './tables';

export interface WriteContext {
  db: LocalDb;
  userId: string;
}

export interface RemoteChange {
  seq: number;
  mutation: Mutation;
}

type Action = 'create' | 'update' | 'delete' | 'restore';
type Query = BatchItem<'sqlite'>;

/** Columns that never get a clock entry (same set as the Worker's applier). */
const UNCLOCKED_COLUMNS = new Set(['id', 'userId', 'createdAt', 'updatedAt', 'clock']);
/** The server refuses other ids as invalid_mutation: clock keys hold the id and are compared byte-wise. */
const PRINTABLE_ASCII = /^[\x21-\x7e]{1,64}$/;

function idColumn(table: SQLiteTable) {
  const col = getTableColumns(table).id;
  if (!col) throw new Error(`Table ${getTableName(table)} has no id column`);
  return col;
}

/** Reads rows once per call and remembers what earlier mutations in the same call did to them. */
class RowCache {
  private rows = new Map<string, Row | null>();
  constructor(private db: LocalDb) {}

  async get(table: SQLiteTable, id: string): Promise<Row | null> {
    const key = `${getTableName(table)}:${id}`;
    if (!this.rows.has(key)) {
      const row = await this.db
        .select()
        .from(table)
        .where(eq(idColumn(table), id))
        .get();
      this.rows.set(key, (row as Row | undefined) ?? null);
    }
    return this.rows.get(key) ?? null;
  }

  put(table: SQLiteTable, id: string, row: Row): void {
    this.rows.set(`${getTableName(table)}:${id}`, row);
  }
}

function insertQuery(db: LocalDb, table: SQLiteTable, row: Row): Query {
  return db.insert(table).values(encodeRow(table, row) as never) as unknown as Query;
}

function updateQuery(db: LocalDb, table: SQLiteTable, id: string, set: Row): Query {
  return db
    .update(table)
    .set(encodeRow(table, set) as never)
    .where(eq(idColumn(table), id)) as unknown as Query;
}

interface HistoryEntry {
  action: Action;
  before: Row | null;
  after: Row;
  sourceModule?: string;
}

function historyQuery(ctx: WriteContext, m: Mutation, entry: HistoryEntry): Query {
  return insertQuery(ctx.db, history, {
    id: newId(),
    userId: ctx.userId,
    actorId: ctx.userId,
    entityType: m.table,
    entityId: m.rowId,
    action: entry.action,
    before: entry.before,
    after: entry.after,
    sourceModule: entry.sourceModule ?? m.source,
    mutationId: m.id,
    at: m.at,
  });
}

const pick = (row: Row, keys: string[]): Row =>
  Object.fromEntries(keys.map((k) => [k, row[k] ?? null]));

/**
 * Same shape the server writes on insert (conventions §14): createdAt = updatedAt = at,
 * deletedAt = null, and a clock entry clockKey(at, id) for every column except id, userId,
 * createdAt, updatedAt and clock (defaults and deletedAt included). Column defaults come from SQL.
 * A seed (ifAbsent) gets floor entries, seedClockKey(id), so any real edit beats it.
 */
function newRow(ctx: WriteContext, table: SQLiteTable, m: Mutation): { row: Row; values: Row } {
  const values = pickColumns(table, m.values ?? {});
  const key = createClockKey(m.at, m);
  const clocked = Object.keys(getTableColumns(table)).filter((k) => !UNCLOCKED_COLUMNS.has(k));
  return {
    values,
    row: {
      ...values,
      id: m.rowId,
      userId: ctx.userId,
      createdAt: m.at,
      updatedAt: m.at,
      deletedAt: null,
      clock: Object.fromEntries(clocked.map((k) => [k, key])),
    },
  };
}

/**
 * What a mutation on an existing row wants to change, or null for a no-op (server rules):
 * a delete of an already-deleted row, or an update with nothing to change.
 */
function incomingValues(table: SQLiteTable, m: Mutation, before: Row): Row | null {
  if (m.op === 'delete') return before.deletedAt ? null : { deletedAt: m.at };
  const values = pickColumns(table, m.values ?? {});
  // An insert on a soft-deleted row restores it; on a live row it merges like an update.
  if (m.op === 'insert' && before.deletedAt) values.deletedAt = null;
  return Object.keys(values).length > 0 ? values : null;
}

interface Merge {
  /** The row after the merge (equal to `before` when nothing won). */
  after: Row;
  queries: Query[];
  changed: boolean;
}

/**
 * Per-field last-write-wins on an existing row, mirroring the Worker's applier: a field takes the
 * incoming value only when clockKey(at, id) is greater than its clock entry (or than createdAt when
 * it has none). An entry equal to the key is this mutation again, so it is skipped. A losing field
 * gets a conflict history row (source 'system', before = losing values, after = kept values).
 * Returns null for a no-op.
 */
function mergeExisting(
  ctx: WriteContext,
  table: SQLiteTable,
  m: Mutation,
  before: Row,
): Merge | null {
  const values = incomingValues(table, m, before);
  if (!values) return null;
  const key = clockKey(m.at, m.id);
  const clock = readClock(before);
  const fresh = Object.fromEntries(Object.entries(values).filter(([f]) => clock[f] !== key));
  const fallback = String(before.createdAt ?? '');
  const { win, lose, clock: next } = mergeFields(clock, fresh, key, fallback);
  const winKeys = Object.keys(win);
  const loseKeys = Object.keys(lose);
  const queries: Query[] = [];
  let after = before;

  if (winKeys.length > 0) {
    const prevUpdated = String(before.updatedAt ?? '');
    const set: Row = { ...win, clock: next, updatedAt: prevUpdated < m.at ? m.at : prevUpdated };
    queries.push(updateQuery(ctx.db, table, m.rowId, set));
    after = { ...before, ...set };
    const action: Action =
      m.op === 'delete'
        ? 'delete'
        : 'deletedAt' in win && win.deletedAt === null && before.deletedAt
          ? 'restore'
          : 'update';
    queries.push(historyQuery(ctx, m, { action, before: pick(before, winKeys), after: win }));
  }
  if (loseKeys.length > 0) {
    queries.push(
      historyQuery(ctx, m, {
        action: 'update',
        before: lose,
        after: pick(before, loseKeys),
        sourceModule: 'system',
      }),
    );
  }
  return { after, queries, changed: winKeys.length > 0 };
}

function checkMutation(m: Mutation): void {
  if (!PRINTABLE_ASCII.test(m.id)) {
    throw new Error(`Mutation id must be 1-64 printable ASCII characters, got '${m.id}'`);
  }
  let iso = '';
  try {
    iso = new Date(m.at).toISOString();
  } catch {
    // reported below
  }
  if (iso !== m.at) throw new Error(`Mutation at must be in toISOString() form, got '${m.at}'`);
  if (m.ifAbsent !== undefined && (m.ifAbsent !== true || m.op !== 'insert')) {
    throw new Error(
      `ifAbsent is for inserts only (and only true), got ${m.op} ${String(m.ifAbsent)}`,
    );
  }
}

/**
 * Insert-if-absent (seeded defaults, conventions §4): an existing row, live or soft-deleted, is
 * never touched, so a renamed default stays renamed and a deleted one stays deleted. Same rule as
 * the server: no row change, no history, and (locally) no outbox entry.
 */
const skipsAsPresent = (m: Mutation, before: Row | null): boolean =>
  m.op === 'insert' && m.ifAbsent === true && before !== null;

async function runBatch(db: LocalDb, queries: Query[]): Promise<void> {
  if (queries.length === 0) return;
  await db.batch(queries as [Query, ...Query[]]);
}

/**
 * Applies mutations made on this device: rows, history, and an outbox row per mutation, all in one
 * SQLite transaction (conventions §5). Semantics match the server (§14): delete = soft delete;
 * insert on a soft-deleted row = restore; insert on a live row = merge; per-field last-write-wins
 * on `${at}|${id}` clock keys. No-ops (a delete of a deleted row, an update with no values, an
 * ifAbsent insert on an existing row) write nothing, not even an outbox row.
 */
export function applyLocal(ctx: WriteContext, mutations: Mutation[]): Promise<void> {
  if (mutations.length === 0) return Promise.resolve();
  return withWriteLock(async () => {
    mutations.forEach(checkMutation);
    const { db } = ctx;
    const cache = new RowCache(db);
    const queries: Query[] = [];
    const touched = new Set<string>();
    const queuedAt = new Date().toISOString();

    for (const m of mutations) {
      const table = tableFor(m.table);
      const before = await cache.get(table, m.rowId);
      if (skipsAsPresent(m, before)) continue;

      if (!before) {
        if (m.op !== 'insert') {
          throw new Error(`Can't ${m.op} ${m.table}/${m.rowId}: row not found on this device`);
        }
        const { row, values } = newRow(ctx, table, m);
        queries.push(insertQuery(db, table, row));
        queries.push(historyQuery(ctx, m, { action: 'create', before: null, after: values }));
        cache.put(table, m.rowId, row);
        touched.add(m.table);
      } else {
        const merge = mergeExisting(ctx, table, m, before);
        if (!merge) continue;
        queries.push(...merge.queries);
        cache.put(table, m.rowId, merge.after);
        if (merge.changed) touched.add(m.table);
      }

      touched.add('history');
      touched.add('outbox');
      queries.push(
        db.insert(outbox).values({
          mutationId: m.id,
          mutation: m,
          createdAt: queuedAt,
          attempts: 0,
          lastError: null,
        }) as unknown as Query,
      );
    }

    await runBatch(db, queries);
    if (touched.size > 0) emitTables(touched);
  });
}

/**
 * Applies changes pulled from the server with the same per-field last-write-wins as the server
 * (conventions §6, §14): a field takes the incoming value only when clockKey(at, id) is greater
 * than its clock entry, or than the row's createdAt when it has none. An equal key is the same
 * mutation again, so pulling a change twice is a no-op. The new cursor is saved in the same
 * transaction. Never touches the outbox.
 */
export function applyRemote(
  ctx: WriteContext,
  changes: RemoteChange[],
  cursor: number,
): Promise<void> {
  return withWriteLock(async () => {
    const { db } = ctx;
    const cache = new RowCache(db);
    const queries: Query[] = [];
    const touched = new Set<string>(['sync_state']);

    for (const { mutation: m } of changes) {
      let table: SQLiteTable;
      try {
        table = tableFor(m.table);
      } catch {
        console.warn(`Tahi sync: skipping a change for unknown table ${m.table}`);
        continue;
      }
      const before = await cache.get(table, m.rowId);
      if (skipsAsPresent(m, before)) continue;

      if (!before) {
        if (m.op !== 'insert') {
          console.warn(
            `Tahi sync: ${m.op} for a row this device never saw (${m.table}/${m.rowId})`,
          );
          continue;
        }
        const { row, values } = newRow(ctx, table, m);
        queries.push(insertQuery(db, table, row));
        queries.push(historyQuery(ctx, m, { action: 'create', before: null, after: values }));
        cache.put(table, m.rowId, row);
        touched.add(m.table);
        touched.add('history');
        continue;
      }

      const merge = mergeExisting(ctx, table, m, before);
      if (!merge || merge.queries.length === 0) continue;
      queries.push(...merge.queries);
      cache.put(table, m.rowId, merge.after);
      touched.add('history');
      if (merge.changed) touched.add(m.table);
    }

    queries.push(setStateQuery(db, 'cursor', String(cursor)) as unknown as Query);
    await runBatch(db, queries);
    emitTables(touched);
  });
}

export interface AdjustedAt {
  id: string;
  at: string;
}

/**
 * Settles a push answer in one transaction: for each mutation the server clamped (`adjusted`), the
 * row's clock entries still equal to clockKey(oldAt, id) become clockKey(at, id), and createdAt /
 * updatedAt that this mutation set to oldAt become `at`, so this device holds the same clock as the
 * server and other devices. Then the `remove` ids leave the outbox.
 */
export function applyPushResult(
  ctx: WriteContext,
  sent: Mutation[],
  remove: string[],
  adjusted: AdjustedAt[],
): Promise<void> {
  return withWriteLock(async () => {
    const { db } = ctx;
    const cache = new RowCache(db);
    const queries: Query[] = [];
    const touched = new Set<string>();
    const byId = new Map(sent.map((m) => [m.id, m]));

    for (const { id, at } of adjusted) {
      const m = byId.get(id);
      if (!m || m.at === at) continue;
      let table: SQLiteTable;
      try {
        table = tableFor(m.table);
      } catch {
        continue;
      }
      const row = await cache.get(table, m.rowId);
      if (!row) continue;
      const oldKey = clockKey(m.at, id);
      const newKey = clockKey(at, id);
      const clock = readClock(row);
      let owned = false;
      for (const field of Object.keys(clock)) {
        if (clock[field] === oldKey) {
          clock[field] = newKey;
          owned = true;
        }
      }
      const set: Row = {};
      // A seed's floor clock entries don't hold `at`, but the row it created still does.
      const createdHere = m.op === 'insert' && row.createdAt === m.at;
      if (owned) set.clock = clock;
      if ((owned || (createdHere && m.ifAbsent)) && row.updatedAt === m.at) set.updatedAt = at;
      if (createdHere) set.createdAt = at;
      if (Object.keys(set).length === 0) continue;
      queries.push(updateQuery(db, table, m.rowId, set));
      cache.put(table, m.rowId, { ...row, ...set });
      touched.add(m.table);
    }

    if (remove.length > 0) {
      queries.push(db.delete(outbox).where(inArray(outbox.mutationId, remove)) as unknown as Query);
      touched.add('outbox');
    }
    await runBatch(db, queries);
    if (touched.size > 0) emitTables(touched);
  });
}
